// A MIDI track's SOUND, carried beside its notes across Audiotool.
//
// A seed's MIDI clip can hold a bounce: what its instrument actually played
// (Live's Serum, say, or an Audiotool synth rendered on the way in). Audiotool
// can't host that instrument, so notes alone would play on a General MIDI
// stand-in — a different sound. So on the way OUT, each MIDI track with bounces
// becomes two lanes:
//
//   "Lead"          the notes, on the stand-in, MUTED — there to edit;
//   "Lead (sound)"  an audio lane playing the bounces where the clips are, with
//                   the track's level, pan and mute — what the seed sounds like.
//
// On the way back IN the pair is recognised by name and folded into one MIDI
// track again: notes from the note lane, bounces from the sound lane (verbatim
// — the same bytes the seed had, so they pool), settings from the sound lane.
// A pair the producer broke (renamed, deleted the sound lane, unmuted and
// edited the notes) simply comes back as what it now is.
//
// Pure: Snapshot and PullPlan in, same shapes out.

import type { AudioClipInfo, AudioTrackInfo, MidiTrackInfo, Snapshot } from "../seed-history.js";
import type { PullAudioClip, PullAudioTrack, PullMidiTrack, PullPlan } from "./pull-plan.js";

export const SOUND_SUFFIX = " (sound)";

export function soundLaneName(midiTrackName: string): string {
  return `${midiTrackName}${SOUND_SUFFIX}`;
}

/** Whether a MIDI track carries any bounce worth sending. */
function hasBounces(t: MidiTrackInfo): boolean {
  return t.arrangementClips.some((c) => Boolean(c.audioKey));
}

/**
 * The snapshot as it should go OUT to Audiotool: MIDI tracks with bounces
 * muted, each followed (after the audio lanes) by its sound lane.
 */
export function withSoundLanes(s: Snapshot): Snapshot {
  const midi = s.midiTracks ?? [];
  if (!midi.some(hasBounces)) return s;
  const taken = new Set(s.audioTracks.map((t) => t.name));
  const soundLanes: AudioTrackInfo[] = [];
  const midiTracks = midi.map((t) => {
    if (!hasBounces(t) || taken.has(soundLaneName(t.name))) return t;
    soundLanes.push({
      name: soundLaneName(t.name),
      mute: t.mute,
      solo: t.solo,
      arm: false,
      mutedViaSolo: t.mutedViaSolo,
      volume: t.volume,
      panning: t.panning,
      groupTrackName: t.groupTrackName,
      takeLanes: [],
      arrangementClips: t.arrangementClips.filter((c) => c.audioKey).map((c): AudioClipInfo => ({
        name: c.name,
        startTime: c.startTime,
        endTime: c.startTime + c.duration,
        duration: c.duration,
        startMarker: 0,
        endMarker: c.duration,
        looping: false,
        loopStart: 0,
        loopEnd: c.duration,
        color: c.color,
        colorHex: c.colorHex,
        muted: c.muted,
        filePath: "",
        warping: false,
        warpMode: { value: 0, name: "Beats" },
        warpMarkerCount: 0,
        audioKey: c.audioKey ?? null,
      })),
    });
    return { ...t, mute: true };
  });
  return {
    ...s,
    audioTracks: [...s.audioTracks, ...soundLanes],
    midiTracks,
    totals: {
      ...s.totals,
      audioTrackCount: s.audioTracks.length + soundLanes.length,
      arrangementClipCount: s.totals.arrangementClipCount + soundLanes.reduce((n, t) => n + t.arrangementClips.length, 0),
    },
  };
}

/** One MIDI clip's bounce, found on its track's sound lane. */
export interface PairedBounce {
  midiTrackIndex: number;
  clipIndex: number;
  clip: PullAudioClip;
}

export interface FoldedPlan extends PullPlan {
  /** Bounces taken from sound lanes, addressed into `midiTracks`. */
  bounces: PairedBounce[];
  /** Per MIDI track (same order): the sound lane it was paired with, if any. */
  soundOf: Array<PullAudioTrack | null>;
}

/**
 * A pulled plan with every note lane / sound lane pair folded back into one
 * MIDI track: the sound lane's settings, its clips as the bounces of the MIDI
 * clips they line up with, and the sound lane itself gone.
 */
export function foldSoundLanes(plan: PullPlan): FoldedPlan {
  const byName = new Map<string, number>();
  plan.audioTracks.forEach((t, i) => {
    if (t.name.endsWith(SOUND_SUFFIX) && !byName.has(t.name)) byName.set(t.name, i);
  });
  const used = new Set<number>();
  const bounces: PairedBounce[] = [];
  const soundOf: Array<PullAudioTrack | null> = [];
  const midiTracks: PullMidiTrack[] = plan.midiTracks.map((m, midiTrackIndex) => {
    const i = byName.get(soundLaneName(m.name));
    if (i === undefined || used.has(i)) {
      soundOf.push(null);
      return m;
    }
    const sound = plan.audioTracks[i]!;
    used.add(i);
    soundOf.push(sound);
    m.clips.forEach((c, clipIndex) => {
      const hit = sound.clips.find((s) => Math.abs(s.startTime - c.startTime) < 1e-6);
      if (hit) bounces.push({ midiTrackIndex, clipIndex, clip: hit });
    });
    return { ...m, settings: sound.settings };
  });
  return {
    ...plan,
    audioTracks: plan.audioTracks.filter((_, i) => !used.has(i)),
    midiTracks,
    bounces,
    soundOf,
  };
}
